import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { EmptyState, ErrorState, errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Inbox, KanbanSquare, Plus, Search, SearchX, Table2 } from 'lucide-react'
import { FilterChip, chipBase, chipOff, chipOn, type ChipOption } from '@/components/queue/FilterChip'
import { KpiStrip } from '@/components/queue/KpiStrip'
import { NewTicketDialog } from '@/components/queue/NewTicketDialog'
import { QueueRow, QueueSkeleton, QueueTableHeader } from '@/components/queue/QueueTable'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useCustomers, useProducts } from '@/hooks/useDirectory'
import { useNow } from '@/hooks/useNow'
import { useTicketCounts } from '@/hooks/useTicketCounts'
import { PAGE_SIZE, useTicketList } from '@/hooks/useTickets'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import {
  FILTER_PRIORITIES,
  FILTER_TYPES,
  chipValueText,
  clearChipFilters,
  hasChipFilters,
  parseQueueFilters,
  setListFilter,
  setTenantFilter,
  toSearchParams,
  type ListFilterKey,
  type QueueFilters,
} from '@/lib/queueFilters'
import { hasOnlyQueue, preferredQueueView, queueBoardId, rememberQueueView } from '@/lib/queueView'
import { priorityLabel, typeLabel } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'

/** Artboard 01: the agent's home screen (customers see their own organisation's tickets). */
export default function QueuePage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isCustomer = user?.kind === 'CUSTOMER'
  const canCreate = usePermission(PERMISSIONS.ticketsCreate)
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => parseQueueFilters(searchParams), [searchParams])
  const filtersKey = toSearchParams(filters).toString()

  const [newOpen, setNewOpen] = useState(false)
  const [search, setSearch] = useState(filters.q)
  const debouncedSearch = useDebouncedValue(search, 300)
  // Cursor stack: [] = first page. Reset whenever the filters change.
  const [cursors, setCursors] = useState<string[]>([])
  const [cursorsFor, setCursorsFor] = useState(filtersKey)
  if (cursorsFor !== filtersKey) {
    setCursorsFor(filtersKey)
    setCursors([])
  }

  const setFilters = (next: QueueFilters) => setSearchParams(toSearchParams(next))

  // Cards first: an agent's plain queue opens as its built-in board unless
  // they picked the table (remembered). Filtered views stay here.
  const navigate = useNavigate()
  const toCards = !isCustomer && preferredQueueView() === 'cards' && hasOnlyQueue(searchParams)
  useEffect(() => {
    if (toCards) navigate(`/boards/${queueBoardId(filters.queue)}`, { replace: true })
  }, [toCards, filters.queue, navigate])

  // URL → input when navigation changes `q` (sidebar link, back button).
  useEffect(() => {
    setSearch((current) => (current.trim() === filters.q ? current : filters.q))
  }, [filters.q])
  // Input → URL, debounced; typing replaces the history entry instead of stacking one per word.
  const latestParams = useRef(searchParams)
  useEffect(() => {
    latestParams.current = searchParams
  }, [searchParams])
  useEffect(() => {
    const current = parseQueueFilters(latestParams.current)
    const q = debouncedSearch.trim()
    if (q !== current.q) setSearchParams(toSearchParams({ ...current, q }), { replace: true })
  }, [debouncedSearch, setSearchParams])

  const now = useNow(30_000)
  const counts = useTicketCounts()
  const list = useTicketList(filters, cursors[cursors.length - 1] ?? null)
  const products = useProducts()
  const customers = useCustomers(!isCustomer)
  const ownCustomer = useCustomers(isCustomer).data?.[0]

  const productOptions: ChipOption[] = (products.data ?? []).map((p) => ({ value: p.key, label: p.name }))
  const customerOptions: ChipOption[] = (customers.data ?? []).map((c) => ({ value: c.slug, label: c.name }))
  // Tenant chip: only while exactly one customer is selected; its options are that client's tenants.
  const singleCustomer = filters.customer.length === 1 ? customers.data?.find((c) => c.slug === filters.customer[0]) : undefined
  const tenantOptions: ChipOption[] = (singleCustomer?.tenants ?? []).map((x) => ({ value: x.id, label: x.name }))
  const labelFrom = (opts: ChipOption[]) => (v: string) => opts.find((o) => o.value === v)?.label ?? v

  const chip = (key: ListFilterKey, label: string, options: ChipOption[], empty: string) => (
    <FilterChip
      key={key}
      label={label}
      valueText={chipValueText(key, filters[key], empty, labelFrom(options))}
      options={options}
      selected={filters[key]}
      onChange={(values) => setFilters(setListFilter(filters, key, values))}
    />
  )

  const singleProduct = filters.product.length === 1 ? productOptions.find((p) => p.value === filters.product[0]) : undefined
  const title = isCustomer
    ? t('queue.customerTitle')
    : singleProduct && filters.queue === 'all'
      ? t('queue.productTitle', { product: singleProduct.label })
      : t(`queue.titles.${filters.queue}`)
  const eyebrow = isCustomer
    ? ownCustomer
      ? t('queue.customerEyebrow', { customer: ownCustomer.name })
      : t('queue.customerEyebrowFallback')
    : t('queue.eyebrow')

  const page = list.data
  const items = page?.items ?? []
  const filtered = hasChipFilters(filters)

  return (
    <Page>
      <PageHeading
        description={eyebrow}
        title={title}
        actions={
          <div className="flex w-full flex-wrap items-center gap-2.5 sm:w-auto">
            {!isCustomer ? (
              <div role="radiogroup" aria-label={t('boards.view.label')} className="inline-flex rounded-md border border-line-strong p-0.5">
                <button
                  type="button"
                  role="radio"
                  aria-checked={false}
                  onClick={() => {
                    rememberQueueView('cards')
                    navigate(`/boards/${queueBoardId(filters.queue)}`)
                  }}
                  className="inline-flex items-center gap-1.5 rounded-[5px] px-2.5 py-1.5 text-meta text-ink-muted hover:text-ink"
                >
                  <KanbanSquare className="size-4" aria-hidden />
                  {t('queue.view.cards')}
                </button>
                <button type="button" role="radio" aria-checked className="inline-flex items-center gap-1.5 rounded-[5px] bg-accent px-2.5 py-1.5 text-meta font-medium text-accent-foreground">
                  <Table2 className="size-4" aria-hidden />
                  {t('queue.view.table')}
                </button>
              </div>
            ) : null}
            <div className="relative w-full sm:w-[300px] sm:shrink-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-placeholder" aria-hidden />
              <input
                type="search"
                aria-label={t('queue.searchLabel')}
                placeholder={t('queue.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-line-strong bg-background py-2 pl-9 pr-3 text-body text-ink placeholder:text-ink-placeholder focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
              />
            </div>
            {canCreate ? (
              <Button onClick={() => setNewOpen(true)} className="gap-1.5">
                <Plus className="size-4" aria-hidden />
                {t('queue.newTicket')}
              </Button>
            ) : null}
          </div>
        }
      />

      {!isCustomer ? <KpiStrip counts={counts.data} /> : null}

      <div className="flex flex-col">
        <div className="flex flex-wrap items-center gap-2 pb-4">
          <button
            type="button"
            onClick={() => setFilters(clearChipFilters(filters))}
            aria-pressed={!filtered}
            className={cn(chipBase, !filtered ? chipOn : chipOff)}
          >
            {t('queue.chips.all')}
          </button>
          {chip(
            'priority',
            t('queue.chips.priority'),
            FILTER_PRIORITIES.map((p) => ({ value: p, label: priorityLabel(p) })),
            t('common.any'),
          )}
          {chip('product', t('queue.chips.product'), productOptions, t('common.any'))}
          {chip(
            'type',
            t('queue.chips.type'),
            FILTER_TYPES.map((v) => ({ value: v, label: typeLabel(v) })),
            t('common.any'),
          )}
          {!isCustomer ? chip('customer', t('queue.chips.customer'), customerOptions, t('common.all')) : null}
          {!isCustomer && tenantOptions.length > 0 ? (
            <FilterChip
              label={t('queue.chips.tenant')}
              valueText={filters.tenant ? labelFrom(tenantOptions)(filters.tenant) : t('common.all')}
              options={tenantOptions}
              selected={filters.tenant ? [filters.tenant] : []}
              onChange={(values) => setFilters(setTenantFilter(filters, values))}
            />
          ) : null}
          <div className="grow" />
          <span className="text-meta text-ink-muted">{t('queue.sortedBySla')}</span>
        </div>

        <div role="table" aria-label={title} aria-busy={list.isFetching} className="overflow-x-auto rounded-lg border border-line">
          <QueueTableHeader showCustomer={!isCustomer} />
          {list.isPending ? (
            <QueueSkeleton />
          ) : list.isError ? (
            <div className="p-4">
              <ErrorState message={t('queue.loadError', { message: errorMessage(list.error) })} onRetry={() => void list.refetch()} />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={filtered ? SearchX : Inbox}
              title={filtered ? t('queue.emptyTitle') : t('queue.emptyQueueTitle')}
              message={filtered ? t('queue.empty') : t('queue.emptyQueue')}
              action={
                filtered ? (
                  <Button variant="outline" size="sm" onClick={() => setFilters(clearChipFilters(filters))}>
                    {t('queue.clearFilters')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div role="rowgroup" className={cn(list.isPlaceholderData && 'opacity-60')}>
              {items.map((ticket) => (
                <QueueRow key={ticket.id} ticket={ticket} now={now} showCustomer={!isCustomer} />
              ))}
            </div>
          )}
        </div>

        {page && items.length > 0 ? (
          <div className="flex items-center justify-between px-3 pt-4">
            <span className="text-meta text-ink-muted">
              {t('queue.showing', { count: cursors.length * PAGE_SIZE + items.length, total: page.total })}
            </span>
            <div className="flex items-center gap-5">
              {cursors.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setCursors((c) => c.slice(0, -1))}
                  className="rounded-sm text-meta font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('queue.previousPage')}
                </button>
              ) : null}
              {page.nextCursor ? (
                <button
                  type="button"
                  onClick={() => setCursors((c) => [...c, page.nextCursor as string])}
                  className="rounded-sm text-meta font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('queue.nextPage')}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {canCreate ? <NewTicketDialog open={newOpen} onOpenChange={setNewOpen} /> : null}
    </Page>
  )
}
